import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import LandingPage from "./pages/LandingPage";
import { InstallPwaPrompt } from "./components/InstallPwaPrompt";
import { DailyCheckinNotifier } from "./components/DailyCheckinNotifier";

// Lazy-load non-landing routes to reduce initial JS payload
const Index = lazy(() => import("./pages/Index"));
const NotFound = lazy(() => import("./pages/NotFound"));
const AuthPage = lazy(() => import("./pages/AuthPage"));
const PaywallPage = lazy(() => import("./pages/PaywallPage"));
const CheckoutReturn = lazy(() => import("./pages/CheckoutReturn"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const OnboardingPage = lazy(() => import("./pages/OnboardingPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const PricingPage = lazy(() => import("./pages/PricingPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const RefundPage = lazy(() => import("./pages/RefundPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const AdminLighthousePage = lazy(() => import("./pages/AdminLighthousePage"));
const AdminSearchConsolePage = lazy(() => import("./pages/AdminSearchConsolePage"));

const queryClient = new QueryClient();

const RouteFallback = () => (
  <div className="min-h-dvh flex items-center justify-center bg-background text-muted-foreground text-sm">
    Loading…
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <InstallPwaPrompt />
          <DailyCheckinNotifier />
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/app" element={<Index />} />
              <Route path="/auth" element={<AuthPage />} />
              <Route path="/paywall" element={<PaywallPage />} />
              <Route path="/checkout/return" element={<CheckoutReturn />} />
              <Route path="/admin" element={<AdminPage />} />
              <Route path="/onboarding" element={<OnboardingPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/refund" element={<RefundPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/admin/lighthouse" element={<AdminLighthousePage />} />
              <Route path="/admin/search-console" element={<AdminSearchConsolePage />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
